/**
 * register.ts —— 把所有 API 模块装进 registry
 *
 * 这个文件是「能力清单」的**唯一装配点**。想加新能力时：
 *   ① 在 api/ 下新建模块，导出 { name, desc, methods }
 *   ② 在这里加一行 registration
 *   ③ 完成 —— 提示词会自动带上它，AI 立刻就会用
 *
 * 不需要改提示词、不需要改文档、不需要改 UI。这就是架构原则 2
 * 「新增一个 API，AI 立刻就会用」在代码里的样子。
 *
 * 关于 import 顺序：注册顺序决定提示词里的排列顺序。
 * 把高频、基础的能力放前面（explain 第一），让模型先看到它们。
 */

import { registry } from './registry';
import { explainApi, EXPLAIN_DEFAULT_METHOD } from './api/explain';
import { navApi } from './api/nav';
import { settingApi } from './api/setting';
import { uiApi } from './api/ui';
import { contactApi } from './api/contact';
import { sessionApi } from './api/session';
import { messageApi } from './api/message';
import { fileApi } from './api/file';
import { deviceApi } from './api/device';
import { appApi } from './api/app';
import { handles } from './handles';

let installed = false;

/**
 * 装配全部能力。重复调用无副作用（幂等）。
 *
 * 为什么做成幂等：热更新（Vite HMR）会重复执行模块顶层代码，
 * 如果注册逻辑写在顶层，刷新一次就多注册一层，提示词越来越长。
 */
export function registerAllApis(): void {
  if (installed) return;
  installed = true;

  // explain 第一个注册 —— 它是 AI 的诚实出口，必须永远可用
  registry.registerObject(explainApi);
  registry.setDefaultMethod(explainApi.name, EXPLAIN_DEFAULT_METHOD);

  // 界面与系统层
  registry.registerObject(navApi);
  registry.registerObject(settingApi);
  registry.registerObject(uiApi);

  // 数据域：按「实体从大到小」排列，模型读起来有层次
  registry.registerObject(contactApi);
  registry.registerObject(sessionApi);
  registry.registerObject(messageApi);
  registry.registerObject(fileApi);

  // 硬件与应用
  registry.registerObject(deviceApi);
  registry.registerObject(appApi);

  // 开发环境打印一份自检，方便确认能力都挂上了
  if (import.meta.env?.DEV) {
    console.info(
      `[灵讯] 已注册 ${registry.listObjects().length} 个对象、${registry.countMethods()} 个方法`,
    );
  }
}

/** 生成提示词用的能力清单。外部只该通过这里拿提示词文本 */
export function describeCapabilities(): string {
  return registry.describeForPrompt();
}

/** 生成提示词用的句柄清单（当前对话里可引用的摘要/照片） */
export function describeHandles(): string {
  return handles.describeForPrompt();
}

export { registry, handles };
